import api from '../../../../api/client';

export const REASON_OPTIONS = [
  { value: 'All', label: 'All Reasons' },
  { value: '4', label: 'Work From Home / Remote' },
  { value: '3', label: 'On Duty / Outdoor Client Visit' },
  { value: '1', label: 'Machine / Scanner Issue' },
  { value: '2', label: 'Card Forgot / Lost' },
  { value: '5', label: 'Other' },
];

export const HOUR_OPTIONS = Array.from({ length: 12 }, (_, i) => {
  const h = String(i + 1).padStart(2, '0');
  return { value: h, label: h };
});

export const MINUTE_OPTIONS = [
  '00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'
].map((m) => ({ value: m, label: m }));

export const AMPM_OPTIONS = [
  { value: 'am', label: 'am' },
  { value: 'pm', label: 'pm' },
];

export const DATE_PRESET_OPTIONS = [
  { value: 'All', label: 'All Dates' },
  { value: 'Today', label: 'Today' },
  { value: 'Yesterday', label: 'Yesterday' },
  { value: 'This Week', label: 'This Week' },
  { value: 'This Month', label: 'This Month' },
  { value: 'Last Month', label: 'Last Month' },
  { value: 'This Quarter', label: 'This Quarter' },
  { value: 'This Financial Year', label: 'This Financial Year' },
  { value: 'Custom', label: 'Custom' },
];

export const STATUS_OPTIONS = [
  { value: 'All', label: 'All Status' },
  { value: 'Pending', label: 'Pending' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Rejected', label: 'Rejected' },
];

function parseDateTimeParts(tStr) {
  if (!tStr) {
    const today = new Date().toISOString().split('T')[0];
    const [y, m, d] = today.split('-');
    return { date: today, dateDisplay: `${d}/${m}/${y}`, hour: '09', minute: '00', ampm: 'am', timeDisplay: '09:00 am' };
  }

  // Handle strings like "2026-09-01 09:10:00" or "2026-09-01T09:10:00"
  if (typeof tStr === 'string' && tStr.includes('-')) {
    const parts = tStr.split(/[ T]/);
    const datePart = parts[0] || '';
    const timePart = parts[1] || '09:00:00';
    const [y, m, day] = datePart.split('-');
    const [h, min] = timePart.split(':');
    let hourNum = parseInt(h || '9', 10);
    const ampm = hourNum >= 12 ? 'pm' : 'am';
    if (hourNum === 0) hourNum = 12;
    else if (hourNum > 12) hourNum -= 12;
    const hourStr = String(hourNum).padStart(2, '0');
    const minStr = String(min || '00').padStart(2, '0');
    return {
      date: datePart,
      dateDisplay: day && m && y ? `${day}/${m}/${y}` : datePart,
      hour: hourStr,
      minute: minStr,
      ampm,
      timeDisplay: `${hourStr}:${minStr} ${ampm}`,
    };
  }

  const d = new Date(tStr);
  if (isNaN(d.getTime())) {
    return { date: '', dateDisplay: '', hour: '09', minute: '00', ampm: 'am', timeDisplay: '09:00 am' };
  }

  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const dateIso = `${year}-${month}-${day}`;
  const dateDisplay = `${day}/${month}/${year}`;

  let h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, '0');
  const ampm = h >= 12 ? 'pm' : 'am';
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  const hourStr = String(h).padStart(2, '0');

  return {
    date: dateIso,
    dateDisplay,
    hour: hourStr,
    minute: m,
    ampm,
    timeDisplay: `${hourStr}:${m} ${ampm}`,
  };
}

function mapBackendToFrontend(item) {
  if (!item) return null;
  const tStr = item.t_datetime || item.TDateTime || item.date || item.Date;
  const parsed = parseDateTimeParts(tStr);
  const empId = item.emp_id || item.EmpID;
  const empName = item.emp_name || item.employee_name || item.Name || (empId ? `Employee #${empId}` : 'Unknown');
  const empCode = item.emp_code || item.EmployeeCode || (empId ? `EMP-${empId}` : '');
  const reasonName = item.reason_name || item.Reason || item.reason || 'Work From Home / Remote';
  const isApproved = item.is_approved ?? item.IsApproved;
  const statusStr = item.status || item.Status || (isApproved === 1 ? 'Approved' : (isApproved === 2 ? 'Rejected' : 'Pending'));

  return {
    id: item.id || item.ID,
    employee: empName,
    emp_name: empName,
    employeeId: empCode,
    emp_code: empCode,
    emp_id: empId,
    company: 'All',
    department: 'All',
    branch: 'All',
    date: parsed.date,
    dateDisplay: parsed.dateDisplay,
    time: parsed.timeDisplay,
    hour: parsed.hour,
    minute: parsed.minute,
    ampm: parsed.ampm,
    dateTimeDisplay: `${parsed.dateDisplay} ${parsed.timeDisplay}`,
    reason: reasonName,
    reason_id: item.reason_id || item.ReasonID || 4,
    remarks: item.remarks || item.Remarks || '',
    status: statusStr,
    gpsLocation: item.gps_in || item.GPSIn || 'Remote Check-in',
    createdDate: parsed.dateDisplay,
    firstPunch: item.first_punch || item.FirstPunch || '',
    lastPunch: item.last_punch || item.LastPunch || '',
  };
}

export const remoteAttendanceApi = {
  getRecords: async (filters = {}) => {
    const params = {
      search: filters.search || filters.searchText || undefined,
      from_date: filters.fromDate || filters.from_date || undefined,
      to_date: filters.toDate || filters.to_date || undefined,
      emp_id: filters.emp_id || filters.employeeId || undefined,
      reason_id: filters.reason_id || filters.reasonId || undefined,
      has_gps: true,
      page: filters.page || 1,
      page_size: filters.pageSize || filters.page_size || 10,
    };
    if (filters.status && filters.status !== 'All') {
      params.approved = filters.status === 'Approved' ? 1 : (filters.status === 'Rejected' ? 2 : 0);
    }

    const { data } = await api.get('/payroll/manual-punch/', { params });
    const rawItems = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
    const items = rawItems.map(mapBackendToFrontend).filter(Boolean);
    const total = data?.total ?? items.length;
    const pageSize = params.page_size;
    const totalPages = Math.ceil(total / pageSize) || 1;

    return {
      items,
      total,
      total_count: total,
      page: params.page,
      page_size: pageSize,
      totalPages,
      total_pages: totalPages,
    };
  },

  getRecordById: async (id) => {
    const { data } = await api.get(`/payroll/manual-punch/${id}`);
    return mapBackendToFrontend(data);
  },

  getReasons: async () => {
    const { data } = await api.get('/payroll/manual-punch/reasons');
    return Array.isArray(data) ? data : [];
  },

  getOptions: async () => {
    const { data } = await api.get('/payroll/manual-punch/options');
    return {
      employees: Array.isArray(data?.employees) ? data.employees : [],
      reasons: Array.isArray(data?.reasons) ? data.reasons : [],
    };
  },

  createRecord: async (payload) => {
    let h = parseInt(payload.hour || '9', 10);
    const m = (payload.minute || '00').padStart(2, '0');
    if ((payload.ampm || 'am').toLowerCase() === 'pm' && h < 12) h += 12;
    if ((payload.ampm || 'am').toLowerCase() === 'am' && h === 12) h = 0;
    const time24 = `${String(h).padStart(2, '0')}:${m}:00`;
    const dateStr = payload.date || new Date().toISOString().split('T')[0];

    const gps = payload.gpsLocation?.trim() || 'Remote Check-in';

    const body = {
      emp_id: Number(payload.emp_id || payload.employeeId || 1),
      t_datetime: `${dateStr}T${time24}`,
      reason_id: Number(payload.reason_id || 4),
      remarks: payload.remarks ? String(payload.remarks).substring(0, 150) : null,
      gps_in: gps,
    };
    const { data } = await api.post('/payroll/manual-punch/', body);
    return data;
  },

  updateRecord: async (id, payload) => {
    let h = parseInt(payload.hour || '9', 10);
    const m = (payload.minute || '00').padStart(2, '0');
    if ((payload.ampm || 'am').toLowerCase() === 'pm' && h < 12) h += 12;
    if ((payload.ampm || 'am').toLowerCase() === 'am' && h === 12) h = 0;
    const time24 = `${String(h).padStart(2, '0')}:${m}:00`;
    const dateStr = payload.date || new Date().toISOString().split('T')[0];

    const gps = payload.gpsLocation?.trim() || 'Remote Check-in';

    const body = {
      emp_id: payload.emp_id ? Number(payload.emp_id) : undefined,
      t_datetime: `${dateStr}T${time24}`,
      reason_id: payload.reason_id ? Number(payload.reason_id) : 4,
      remarks: payload.remarks !== undefined ? (payload.remarks ? String(payload.remarks).substring(0, 150) : null) : undefined,
      gps_in: gps,
    };
    const { data } = await api.put(`/payroll/manual-punch/${id}`, body);
    return data;
  },

  updateStatus: async (id, status) => {
    const is_approved = status === 'Approved' ? 1 : (status === 'Rejected' ? 2 : 0);
    const { data } = await api.patch(`/payroll/manual-punch/${id}/approve`, { is_approved });
    return data;
  },

  deleteRecord: async (id) => {
    const { data } = await api.delete(`/payroll/manual-punch/${id}`);
    return data;
  },

  downloadTemplate: async () => {
    const response = await api.get('/payroll/manual-punch/template', {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'Remote_Attendance_Template.xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  uploadBulk: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await api.post('/payroll/manual-punch/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return data;
  },
};
